import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAuthNotifications } from '../../hooks/useAuthNotifications';
import { supabase } from '../../lib/supabase';

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user, loading: authLoading, initialized } = useAuth();
  const { notifyLogin } = useAuthNotifications();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (initialized && user && !authLoading) {
      console.log('Utilizador autenticado, redirecionando para CMS...');
      const from = (location.state as any)?.from?.pathname || '/cms';
      navigate(from, { replace: true });
    }
  }, [user, authLoading, initialized, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setSuccess(false);

    try {
      console.log('Iniciando processo de login...');
      const loggedUser = await signIn(email, password);
      console.log('Login processado com sucesso, usuário:', loggedUser);
      
      if (loggedUser) {
        console.log('Usuário confirmado como autenticado:', loggedUser);
        console.log('Criando notificação de login para:', email);
        await notifyLogin(email, loggedUser);
        console.log('Notificação de login criada com sucesso');
        setSuccess(true);
      } else {
        console.error('Usuário não está autenticado após login');
        throw new Error('Falha na autenticação');
      }
    } catch (err: any) {
      console.error('Erro durante o login:', err);
      setError(err.message || 'Credenciais inválidas. Por favor, tente novamente.');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (!initialized || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card p-8 rounded-lg shadow-lg max-w-md w-full"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Bem-vindo ao CMS</h1>
          <p className="text-gray-400">Faça login para continuar</p>
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-primary/10 text-primary p-4 rounded-lg mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <p className="text-sm">Login bem-sucedido! Redirecionando...</p>
          </div>
        )}

        <div className="bg-secondary/50 p-4 rounded-lg mb-6 flex items-start gap-2">
          <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium mb-1">Credenciais de Demonstração:</p>
            <p>Email: admin@example.com</p>
            <p>Senha: admin123</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              required
              disabled={loading || success}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium mb-2">
              Senha
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              required
              disabled={loading || success}
            />
          </div>

          <button
            type="submit"
            disabled={loading || success}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : success ? (
              <>
                <CheckCircle className="w-5 h-5" />
                Sucesso!
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                Entrar
              </>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default SignIn;